import React, { useState, useEffect } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import axios from "axios";

// material-ui icons
import Person from "@material-ui/icons/Person";
import Edit from "@material-ui/icons/Edit";
import Close from "@material-ui/icons/Close";
import Check from "@material-ui/icons/Check";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Table from "components/Table/Table";
import Button from "components/CustomButtons/Button";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import AssessmentCard from "./AssessmentCard";

import styles from "assets/jss/elite-feats-cms/views/extendedTablesStyle";

import AppContext from "../../AppContext";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

interface ToolTotals {
  [key: string]: number;
}

interface ToolsTableProps {
  totalPurchased: ToolTotals;
  totalUsed: ToolTotals;
  pending: ToolTotals;
  completed: ToolTotals;
  subscriptionData?: { active?: boolean; totalAvailable: number };
  isMobile?: boolean;
  triggerReload?: () => void;
  tourRun?: boolean;
  setTriggerStep?: (step: number) => void;
}

export default function ExtendedTables(props: ToolsTableProps): React.ReactElement {
  const { t } = useTranslation();
  let assignment = JSON.parse(
    localStorage.getItem("assignment") as string
  ) as Record<string, boolean>;
  let order = JSON.parse(localStorage.getItem("order") as string) as string[];
  const {
    totalPurchased,
    totalUsed,
    pending,
    completed,
    subscriptionData,
    isMobile,
  } = props;
  const cartContext = React.useContext(AppContext);
  const initialChecked: string[] = [];
  for (let number in assignment) {
    if (assignment[number] === true) {
      initialChecked.push(number);
    }
  }

  const [checked, setChecked] = useState<string[]>(initialChecked);
  const [subscriptionAvailable, setSubscriptionAvailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [effectiveAvailableUnits, setEffectiveAvailableUnits] = useState({
    tool2_1: totalPurchased.tool2_1 - totalUsed.tool2_1,
    tool2_2: totalPurchased.tool2_2 - totalUsed.tool2_2,
    tool2_3: totalPurchased.tool2_3 - totalUsed.tool2_3,
  });

  // Fetch subscription data
  useEffect(() => {
    try {
      setLoading(true);

      const availableUnits =
        subscriptionData && subscriptionData.active
          ? subscriptionData.totalAvailable
          : 0;

      setSubscriptionAvailable(availableUnits);

      // Update the effective available units
      setEffectiveAvailableUnits({
        tool2_1:
          totalPurchased.tool2_1 -
          totalUsed.tool2_1 +
          (availableUnits > 0 ? 1 : 0),
        tool2_2:
          totalPurchased.tool2_2 -
          totalUsed.tool2_2 +
          (availableUnits > 0 ? 1 : 0),
        tool2_3:
          totalPurchased.tool2_3 -
          totalUsed.tool2_3 +
          (availableUnits > 0 ? 1 : 0),
      });
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      // Fallback to just purchased - used
      setEffectiveAvailableUnits({
        tool2_1: totalPurchased.tool2_1 - totalUsed.tool2_1,
        tool2_2: totalPurchased.tool2_2 - totalUsed.tool2_2,
        tool2_3: totalPurchased.tool2_3 - totalUsed.tool2_3,
      });
    } finally {
      setLoading(false);
    }
  }, [totalPurchased, totalUsed]);

  useEffect(() => {
    if (!order.length && checked.length) {
      setChecked([]);
    }
  });

  const handleToggle = (value: string | number) => {
    const currentIndex = checked.indexOf(value.toString());
    const orderIndex = order.indexOf(value.toString());
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value.toString());
      order.push(value.toString());
      assignment[`${value}`] = true;
    } else {
      newChecked.splice(currentIndex, 1);
      order.splice(orderIndex, 1);
      assignment[`${value}`] = false;
    }
    setChecked(newChecked);
    localStorage.setItem("assignment", JSON.stringify(assignment));
    localStorage.setItem("order", JSON.stringify(order));
    props.triggerReload!();
  };

  const classes = useStyles();
  const fillButtons = [
    { color: "info", icon: Person },
    { color: "success", icon: Edit },
    { color: "danger", icon: Close },
  ].map((prop, key) => {
    return (
      <Button color={prop.color as React.ComponentProps<typeof Button>["color"]} className={classes.actionButton} key={key}>
        <prop.icon className={classes.icon} />
      </Button>
    );
  });

  const simpleButtons = [
    { color: "info", icon: Person },
    { color: "success", icon: Edit },
    { color: "danger", icon: Close },
  ].map((prop, key) => {
    return (
      <Button
        color={prop.color as React.ComponentProps<typeof Button>["color"]}
        simple
        className={classes.actionButton}
        key={key}
      >
        <prop.icon className={classes.icon} />
      </Button>
    );
  });

  const roundButtons = [
    { color: "info", icon: Person },
    { color: "success", icon: Edit },
    { color: "danger", icon: Close },
  ].map((prop, key) => {
    return (
      <Button
        round
        color={prop.color as React.ComponentProps<typeof Button>["color"]}
        className={classes.actionButton + " " + classes.actionButtonRound}
        key={key}
      >
        <prop.icon className={classes.icon} />
      </Button>
    );
  });

  // Tools data for both card and table views
  const tools = [
    {
      number: "2.1",
      name: t("assignments:assessmentNames.tool2_1"),
      purchased: totalPurchased.tool2_1,
      used: totalUsed.tool2_1,
      available: totalPurchased.tool2_1 - totalUsed.tool2_1,
      pending: pending.tool2_1,
      completed: completed.tool2_1,
      effectiveAvailable: effectiveAvailableUnits.tool2_1,
      key: "tool2_1",
    },
    {
      number: "2.2",
      name: t("assignments:assessmentNames.tool2_2"),
      purchased: totalPurchased.tool2_2,
      used: totalUsed.tool2_2,
      available: totalPurchased.tool2_2 - totalUsed.tool2_2,
      pending: pending.tool2_2,
      completed: completed.tool2_2,
      effectiveAvailable: effectiveAvailableUnits.tool2_2,
      key: "tool2_2",
    },
    {
      number: "2.3",
      name: t("assignments:assessmentNames.tool2_3"),
      purchased: totalPurchased.tool2_3,
      used: totalUsed.tool2_3,
      available: totalPurchased.tool2_3 - totalUsed.tool2_3,
      pending: pending.tool2_3,
      completed: completed.tool2_3,
      effectiveAvailable: effectiveAvailableUnits.tool2_3,
      key: "tool2_3",
    },
  ];

  // If mobile, render card view
  if (isMobile) {
    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card plain style={{ marginTop: -10, marginBottom: 0 }}>
            <CardBody>
              {tools.map((tool) => (
                <AssessmentCard
                  key={tool.number}
                  name={tool.name}
                  purchased={tool.purchased}
                  used={tool.used}
                  available={`${tool.available}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`}
                  pending={tool.pending}
                  completed={tool.completed}
                  action={
                    tool.effectiveAvailable > 0 ? (
                      <Checkbox
                        key="key"
                        tabIndex={-1}
                        style={{ padding: 7 }}
                        onClick={() => handleToggle(tool.number)}
                        checked={assignment[tool.number]}
                        checkedIcon={<Check className={classes.checkedIcon} />}
                        icon={<Check className={classes.uncheckedIcon} />}
                        classes={{
                          checked: classes.checkedDarkBlue,
                          root: classes.checkRoot,
                        }}
                      />
                    ) : (
                      <Button
                        color="tool2"
                        onClick={() => {
                          let currentCart = JSON.parse(
                            localStorage.getItem("cart") as string
                          ) as Record<string, number>;
                          currentCart[tool.key] += 1;
                          localStorage.setItem(
                            "cart",
                            JSON.stringify(currentCart)
                          );
                          cartContext.setCart(currentCart);
                        }}
                        round
                        size="sm"
                        style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      >
                        {t("assignments:addToCart")}
                      </Button>
                    )
                  }
                />
              ))}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }

  // Desktop: render table view
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card plain style={{ marginTop: -10, marginBottom: 0 }}>
          <CardBody>
            <Table
              tableHead={[
                t("assignments:tableHeaders.assessment"),
                t("assignments:tableHeaders.purchased"),
                t("assignments:tableHeaders.used"),
                t("assignments:tableHeaders.available"),
                t("assignments:tableHeaders.pending"),
                t("assignments:tableHeaders.completed"),
                t("assignments:tableHeaders.assign"),
              ]}
              tableData={[
                [
                  t("assignments:assessmentNames.tool2_1"),
                  `${totalPurchased.tool2_1}`,
                  `${totalUsed.tool2_1}`,
                  `${totalPurchased.tool2_1 - totalUsed.tool2_1}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool2_1}`,
                  `${completed.tool2_1}`,
                  effectiveAvailableUnits.tool2_1 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(2.1)}
                      checked={assignment["2.1"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedYellow,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="warning"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart") as string
                        ) as Record<string, number>;
                        currentCart["tool2_1"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
                [
                  t("assignments:assessmentNames.tool2_2"),
                  `${totalPurchased.tool2_2}`,
                  `${totalUsed.tool2_2}`,
                  `${totalPurchased.tool2_2 - totalUsed.tool2_2}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool2_2}`,
                  `${completed.tool2_2}`,
                  effectiveAvailableUnits.tool2_2 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(2.2)}
                      checked={assignment["2.2"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedYellow,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="warning"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart") as string
                        ) as Record<string, number>;
                        currentCart["tool2_2"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
                [
                  t("assignments:assessmentNames.tool2_3"),
                  `${totalPurchased.tool2_3}`,
                  `${totalUsed.tool2_3}`,
                  `${totalPurchased.tool2_3 - totalUsed.tool2_3}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool2_3}`,
                  `${completed.tool2_3}`,
                  effectiveAvailableUnits.tool2_3 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(2.3)}
                      checked={assignment["2.3"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedYellow,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="warning"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart") as string
                        ) as Record<string, number>;
                        currentCart["tool2_3"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
              ]}
              customCellClasses={[
                classes.left,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
              ]}
              customClassesForCells={[0, 1, 2, 3, 4, 5, 6]}
              customHeadCellClasses={[
                classes.left,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
              ]}
              customHeadClassesForCells={[0, 1, 2, 3, 4, 5, 6]}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
